import {
  QBox,
  QPerson,
  QStack,
  QTag,
  QTagProps,
  QText,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../../../../hooks/useFormatDate';
import {
  Approval,
  PeriodicReviewDetails,
  PeriodicReviewDetailsStatus,
} from './types';

const statusToColorMap = {
  [PeriodicReviewDetailsStatus.approved]: 'purple',
  [PeriodicReviewDetailsStatus.declined]: 'red',
  [PeriodicReviewDetailsStatus.in_progress]: 'blue',
};

export const mapStatus = (approvalStatus: Approval['approved']) => {
  if (approvalStatus) {
    return 'approved';
  } else if (approvalStatus === false) {
    return 'declined';
  } else {
    return 'default';
  }
};

export const ReviewDetails = ({
  details,
}: {
  details?: PeriodicReviewDetails;
}) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  if (!details) {
    return null;
  }

  return (
    <QStack direction="column" spacing="4">
      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.status')}
        </QText>
        <QBox fontSize="sm" width="50%">
          <QTag
            variantColor={
              statusToColorMap[details.status] as QTagProps['variantColor']
            }
          >
            {details.status}
          </QTag>
        </QBox>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.sentForApprovalBy')}
        </QText>
        <QText fontSize="sm" width="50%">
          {details.sent_by}
        </QText>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.sentForApprovalAt')}
        </QText>
        <QText fontSize="sm" width="50%">
          {details.sent_at}
        </QText>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.needsUpdating')}
        </QText>
        <QText fontSize="sm" width="50%">
          {t('labels.no')}
        </QText>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.comment')}
        </QText>
        <QText fontSize="sm" width="50%">
          {details.comment}
        </QText>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.reviewPeriodLabel')}
        </QText>
        <QText fontSize="sm" width="50%">
          {details.review_period}
        </QText>
      </QStack>

      <QStack direction="row">
        <QText weight="semibold" fontSize="sm" width="50%">
          {t('labels.approvals')}
        </QText>
        <QStack fontSize="sm" width="50%" spacing={1}>
          {details.approvals.map((approval) => {
            return (
              <QStack
                key={`${approval.user.id}-${approval.timestamp}`}
                spacing={1}
              >
                <QPerson
                  status={mapStatus(approval.approved)}
                  fullName={approval.user.full_name}
                  actionTimestamp={
                    approval.timestamp
                      ? formatDate(new Date(approval.timestamp * 1000), true)
                      : undefined
                  }
                />
                {approval.comment && (
                  <QText as="i" fontSize="xs" mt={1} color={'gray.700'}>
                    {approval.comment}
                  </QText>
                )}
              </QStack>
            );
          })}
        </QStack>
      </QStack>
    </QStack>
  );
};
